import { PhoneNumberType } from '@getstep/sdk/dist/util/Random'
import { ErrorMessage } from '@hookform/error-message'
import { useRouter } from 'next/router'
import type { FunctionComponent, PropsWithChildren } from 'react'
import { useEffect } from 'react'
import { useController, useForm } from 'react-hook-form'

import { TelephoneInput } from '@components/input/telephone-input'
import { Legend } from '@contentful-components/forms/legend'
import type { FormProps } from '@contentful-components/forms/props'
import { CustomFieldId, useField } from '@graphql/hooks/field'
import { CustomFieldsetId } from '@graphql/hooks/form-page'
import { CustomModalId } from '@graphql/hooks/modal'
import { useDevMenuItem } from '@lib/hooks/useDevMenuItem'
import { usePersonalizations } from '@lib/hooks/usePersonalizations'
import { DevActionGroup, DevActionName } from '@lib/store/ui/dev-menu'
import { modalStore } from '@lib/store/ui/modal'
import { useSession } from '@lib/store/useStores'
import { PhoneVerificationStatus } from '@lib/utils/login'
import { LINK, LocalLink } from '@shared/util/link'
import { validateTelephoneInput } from '@shared/util/phone'
import { generatePhone } from '@shared/util/tests'

/**
 * This form lets the user enter and verify their phone number.
 */
export const EnterPhone: FunctionComponent<
    PropsWithChildren<FormProps<Values>>
> = ({ next, title, renderSubmit }) => {
    const router = useRouter()
    const {
        login,
        phoneVerificationStore: store,
        phoneVerificationStore: { valueToVerify = '', startVerification },
        // since this is inside <Fieldset/>, it's automatically inside withSession(...)
    } = useSession()

    const {
        handleSubmit,
        setError,
        control,
        watch,
        setValue,
        formState: { isSubmitting, errors, submitCount },
    } = useForm<Values>({
        mode: 'onTouched',
        defaultValues: { phone: valueToVerify },
    })

    const personalizations = usePersonalizations()

    const { label, placeholder, errorTexts } = useField(
        CustomFieldId.LoginPhone,
    )

    const {
        field,
        fieldState: { invalid },
    } = useController({
        control,
        name: 'phone',
        rules: {
            required: {
                value: true,
                message: errorTexts.required,
            },
            validate: async (phone) => {
                const isValid = await validateTelephoneInput(phone)
                return isValid || errorTexts.validate
            },
        },
    })

    const value = watch('phone')

    useEffect(() => {
        store.setValueToVerify(value)
    }, [store, value])

    useDevMenuItem({
        group: DevActionGroup.PAGE,
        name: DevActionName.FILL_PHONE,
        message: 'Filled the form with a (###) 555-#### number',
        async onActivate() {
            setValue('phone', await generatePhone(PhoneNumberType.Normal), {
                shouldDirty: true,
                shouldValidate: true,
            })
        },
    })

    return (
        <form
            onSubmit={handleSubmit(async ({ phone }) => {
                const answer = await modalStore.promptById(
                    CustomModalId.LoginConfirmPhone,
                    personalizations,
                )

                if (!answer) return

                const result = await startVerification(phone)

                switch (result.status) {
                    case PhoneVerificationStatus.SECOND_FACTOR_REQUIRED: {
                        await router.push(
                            LINK.verifyOTP(next).serialize(router),
                            undefined,
                            { shallow: true }, // this lets us not run getInitialProps for that page
                        )
                        return
                    }
                    case PhoneVerificationStatus.SUCCESS: {
                        if (result.auth) {
                            await login()
                        }
                        await router.replace(
                            new LocalLink({
                                base: next ?? LINK.home().serialize(router),
                            }).serialize(router),
                        )
                        return
                    }
                    case PhoneVerificationStatus.ERROR: {
                        setError('phone', {
                            message: result.error.userFriendlyDescription,
                        })
                        return
                    }
                    default: {
                        setError('phone', {
                            message: errorTexts.connectionFailure,
                        })
                    }
                }
            })}
        >
            <Legend
                preset='primary'
                customFieldsetId={CustomFieldsetId.LoginEnterEmail}
            >
                {title}
            </Legend>

            <TelephoneInput
                {...field}
                autoFocus
                preset='material'
                disabled={isSubmitting}
                validationCount={submitCount}
                size='medium'
                valid={!invalid}
                label={label}
                placeholder={placeholder}
                error={<ErrorMessage errors={errors} name='phone' />}
                data-testid='login.phone.input'
            />

            {renderSubmit(control)}
        </form>
    )
}

interface Values {
    phone: string
}
